import React, { useState, useEffect } from 'react';
import { Ticket, Plus, MessageSquare, Clock, CheckCircle, AlertCircle, Search, User } from 'lucide-react';
import { ticketService } from '../../services/ticketService';
import LoadingSpinner from '../../components/LoadingSpinner';
import DataErrorFallback from '../../components/DataErrorFallback';

export default function TicketCreationManagement() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Create ticket form state
  const [formData, setFormData] = useState({
    category: 'technical_issues',
    subject: '',
    description: '',
    priority: 'medium'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadUserTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      loadTicketMessages();
    }
  }, [selectedTicket]);

  const loadUserTickets = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await ticketService?.getUserTickets();
      setTickets(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTicketMessages = async () => {
    try {
      const data = await ticketService?.getTicketMessages(selectedTicket?.id);
      setMessages(data);
    } catch (err) {
      setError(err?.message);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData?.subject?.trim()) errors.subject = 'Subject is required';
    if (!formData?.description?.trim()) errors.description = 'Description is required';
    if (formData?.subject?.length > 200) errors.subject = 'Subject must be less than 200 characters';
    if (formData?.description?.length < 20) errors.description = 'Description must be at least 20 characters';
    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleCreateTicket = async (e) => {
    e?.preventDefault();
    
    if (!formData?.subject || !formData?.description || !formData?.category) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      // Service expects camelCase and converts to snake_case internally
      await ticketService?.createTicket({
        subject: formData?.subject,
        description: formData?.description,
        category: formData?.category,
        priority: formData?.priority
      });
      
      setShowCreateModal(false);
      setFormData({
        subject: '',
        description: '',
        category: 'technical_issues',
        priority: 'medium'
      });
      
      // Reload tickets
      loadUserTickets();
      
    } catch (err) {
      setError(err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim()) return;

    try {
      await ticketService?.addTicketMessage(selectedTicket?.id, newMessage);
      setNewMessage('');
      await loadTicketMessages();
    } catch (err) {
      setError(err?.message);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="w-5 h-5 text-blue-600" />,
      in_progress: <Clock className="w-5 h-5 text-purple-600" />,
      resolved: <CheckCircle className="w-5 h-5 text-green-600" />,
      closed: <CheckCircle className="w-5 h-5 text-gray-600" />
    };
    return icons?.[status] || <Ticket className="w-5 h-5" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatCategory = (category) => {
    return category?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const filteredTickets = tickets?.filter(ticket =>
    searchQuery === '' ||
    ticket?.ticketNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    ticket?.subject?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;
  if (error && !tickets?.length) return <DataErrorFallback message={error} onRetry={loadUserTickets} />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Support Tickets</h1>
            <p className="text-gray-600">Track and manage your support requests</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create New Ticket
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tickets by number or subject..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>
        </div>

        {/* Tickets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTickets?.map((ticket) => (
            <div key={ticket?.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-gray-500">
                      {ticket?.ticketNumber}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      ticket?.status === 'open' ? 'bg-blue-100 text-blue-700' :
                      ticket?.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                      ticket?.status === 'resolved'? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {ticket?.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {ticket?.subject}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {ticket?.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(ticket.createdAt)?.toLocaleDateString()}
                    </span>
                    {ticket?.assignedAgent && (
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        Assigned to: {ticket?.assignedAgent?.fullName}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedTicket(ticket);
                  setShowDetailModal(true);
                }}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View Details →
              </button>
            </div>
          ))}
        </div>

        {filteredTickets?.length === 0 && (
          <div className="text-center py-12">
            <Ticket className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery ? 'Try a different search term' : 'Create your first support ticket to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Create Ticket
              </button>
            )}
          </div>
        )}

        {/* Create Ticket Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Create New Support Ticket</h2>
              </div>
              <form onSubmit={handleCreateTicket} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData?.category}
                    onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
                  >
                    <option value="technical_issues">Technical Issues</option>
                    <option value="billing">Billing</option>
                    <option value="events">Events</option>
                    <option value="marketplace">Marketplace</option>
                    <option value="account">Account</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors?.subject ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Brief description of your issue"
                    value={formData?.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e?.target?.value })}
                  />
                  {formErrors?.subject && (
                    <p className="text-red-600 text-sm mt-1">{formErrors?.subject}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors?.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    rows="6"
                    placeholder="Provide detailed information about your issue (minimum 20 characters)"
                    value={formData?.description}
                    onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  />
                  {formErrors?.description && (
                    <p className="text-red-600 text-sm mt-1">{formErrors?.description}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {formData?.description?.length} characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData?.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e?.target?.value })}
                  >
                    <option value="low">Low - General inquiry</option>
                    <option value="medium">Medium - Issue affecting usage</option>
                    <option value="high">High - Major problem</option>
                    <option value="critical">Critical - Service is broken</option>
                  </select>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setFormData({
                        category: 'technical_issues',
                        subject: '',
                        description: '',
                        priority: 'medium'
                      });
                      setFormErrors({});
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Ticket Details Modal */}
        {showDetailModal && selectedTicket && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(selectedTicket?.status)}
                      <h2 className="text-2xl font-bold text-gray-900">{selectedTicket?.ticketNumber}</h2>
                    </div>
                    <p className="text-gray-600">{selectedTicket?.subject}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedTicket(null);
                      setMessages([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                <div className="flex gap-2 mt-4">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedTicket?.status)}`}>
                    {selectedTicket?.status?.replace('_', ' ')?.toUpperCase()}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getPriorityColor(selectedTicket?.priority)}`}>
                    {selectedTicket?.priority?.toUpperCase()}
                  </span>
                  <span className="px-3 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-800">
                    {formatCategory(selectedTicket?.category)}
                  </span>
                </div>
              </div>

              {/* Ticket Description */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Original Request</h3>
                <p className="text-gray-900 whitespace-pre-wrap">{selectedTicket?.description}</p>
              </div>

              {/* Messages Thread */}
              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Conversation</h3>
                <div className="space-y-4">
                  {messages?.map((message) => (
                    <div
                      key={message?.id}
                      className={`flex gap-3 ${message?.sender?.role === 'admin' ? 'flex-row' : 'flex-row-reverse'}`}
                    >
                      <div className="flex-shrink-0">
                        {message?.sender?.avatarUrl ? (
                          <img
                            src={message?.sender?.avatarUrl}
                            alt={message?.sender?.fullName}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-600 font-medium">
                              {message?.sender?.fullName?.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className={`flex-1 ${message?.sender?.role === 'admin' ? 'text-left' : 'text-right'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{message?.sender?.fullName}</span>
                          {message?.sender?.role === 'admin' && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Support</span>
                          )}
                        </div>
                        <div className={`inline-block px-4 py-2 rounded-lg ${
                          message?.sender?.role === 'admin' ?'bg-gray-100 text-gray-900' :'bg-blue-600 text-white'
                        }`}>
                          <p className="text-sm">{message?.messageContent}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(message.createdAt)?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              {selectedTicket?.status !== 'closed' && (
                <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-200">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e?.target?.value)}
                    />
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                      disabled={!newMessage?.trim()}
                    >
                      <MessageSquare className="w-5 h-5" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}